import { useState, useCallback } from 'react';
import { InMemoryCache, useApolloClient } from '@apollo/client';
import { AttendeeConnections } from '@state/index';
import { operations } from '@components/connections/ConnectionAlert';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { BaseConnectionMutationInterface, DeclineConnectionReturn } from '../types';
import {
  removeAttendeeFromPendingConnections,
  updateAttendeeToNotConnected,
  decrementPendingConnectionCount,
  onQueryUpdated
} from '../cache';
import useConnectionActionAnalytics from './useConnectionActionAnalytics';

const LOG = LoggerFactory.create('useDeclineConnection');

const useDeclineConnection = ({
  setConnectionAlert,
  analyticPageId,
  showSuccessAlert = true
}: BaseConnectionMutationInterface): DeclineConnectionReturn => {
  const [loadingDeclineConnection, setLoading] = useState(false);
  const [errorDeclineConnection, setError] = useState(false);
  const { mutate, cache: apolloCache } = useApolloClient();
  const trackConnectionAction = useConnectionActionAnalytics();

  const declineAttendeeConnection = useCallback(
    async (attendeeId, contactId) => {
      setLoading(true);
      setError(false);

      try {
        await mutate({
          mutation: AttendeeConnections.Mutations.DECLINE_ATTENDEE_CONNECTION,
          variables: {
            contactId
          },
          update(cache) {
            updateAttendeeToNotConnected(attendeeId, cache as InMemoryCache);
            removeAttendeeFromPendingConnections(attendeeId, cache as InMemoryCache);
            decrementPendingConnectionCount(cache as InMemoryCache);
          },
          onQueryUpdated(watchedQuery) {
            return onQueryUpdated(watchedQuery, apolloCache as InMemoryCache);
          }
        });

        if (showSuccessAlert) {
          setConnectionAlert({ type: operations.declineSuccess });
        }

        trackConnectionAction.decline({
          attendeeId,
          contactId,
          analyticPageId
        });
      } catch (e) {
        LOG.error('Failed to decline attendee connection', { contactId, attendeeId }, e);
        setConnectionAlert({ type: operations.declineFailure });
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError, mutate, setConnectionAlert, analyticPageId, trackConnectionAction, apolloCache]
  );

  return { declineAttendeeConnection, loadingDeclineConnection, errorDeclineConnection };
};

export default useDeclineConnection;
